import type {
  DataBuilderCancelPostBody,
  DataBuilderCancelPostResponse,
  DataBuilderEstimatePostBody,
  DataBuilderEstimatePostResponse,
  DataBuilderGetSettingsPostBody,
  DataBuilderGetSettingsPostResponse,
  DataBuilderRootGetResponse,
  DataBuilderSharePostBody,
  DataBuilderSharePostResponse,
  DataBuilderShareRedeemPostBody,
  DataBuilderShareRedeemPostResponse,
  DataBuilderStatusGetResponse,
  DataBuilderSubmitPostBody,
  DataBuilderSubmitPostResponse,
  DataBuilderUpdateSettingsPostBody,
  DataBuilderVerifyPostBody,
  DataBuilderVerifyPostResponse,
} from './data-builder-api.types';
import { DATA_BUILDER_ENDPOINTS, withUserId } from './data-builder-endpoints';
import { fetchJson } from './fetch-util';

export const fetchDataBuilderResults =
  (): Promise<DataBuilderRootGetResponse> =>
    fetchJson<DataBuilderRootGetResponse>(DATA_BUILDER_ENDPOINTS.root, {
      method: 'GET',
    });

export const fetchDataBuilderStatus = (
  userId?: string
): Promise<DataBuilderStatusGetResponse> =>
  fetchJson<DataBuilderStatusGetResponse>(
    withUserId(DATA_BUILDER_ENDPOINTS.status, userId),
    { method: 'GET' }
  );

// When wrapping in useMutation, decorate the options with
// withDataBuilderMutationPolicy so the 422-skip + Kibana logging policy
// stays attached.
export const verifyDataBuilderPayload = (
  payload: DataBuilderVerifyPostBody
): Promise<DataBuilderVerifyPostResponse> =>
  fetchJson<DataBuilderVerifyPostResponse>(DATA_BUILDER_ENDPOINTS.verify, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const submitDataBuilderPayload = (
  payload: DataBuilderSubmitPostBody
): Promise<DataBuilderSubmitPostResponse> =>
  fetchJson<DataBuilderSubmitPostResponse>(DATA_BUILDER_ENDPOINTS.submit, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const estimateDataBuilderPayload = (
  payload: DataBuilderEstimatePostBody
): Promise<DataBuilderEstimatePostResponse> =>
  fetchJson<DataBuilderEstimatePostResponse>(DATA_BUILDER_ENDPOINTS.estimate, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const cancelDataBuilderRun = (
  body: DataBuilderCancelPostBody
): Promise<DataBuilderCancelPostResponse> =>
  fetchJson<DataBuilderCancelPostResponse>(DATA_BUILDER_ENDPOINTS.cancel, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const getDataBuilderSettings = (
  body: DataBuilderGetSettingsPostBody
): Promise<DataBuilderGetSettingsPostResponse> =>
  fetchJson<DataBuilderGetSettingsPostResponse>(
    DATA_BUILDER_ENDPOINTS.getSettings,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );

// When wrapping in useMutation, decorate the options with
// withDataBuilderMutationPolicy so the 422-skip + Kibana logging policy
// stays attached. Returns no body (200/201 empty); see fetch-util empty-body handling.
export const updateDataBuilderSettings = (
  body: DataBuilderUpdateSettingsPostBody
): Promise<void> =>
  fetchJson<void>(DATA_BUILDER_ENDPOINTS.updateSettings, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const shareDataBuilderRun = (
  body: DataBuilderSharePostBody
): Promise<DataBuilderSharePostResponse> =>
  fetchJson<DataBuilderSharePostResponse>(DATA_BUILDER_ENDPOINTS.share, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const redeemDataBuilderShare = (
  body: DataBuilderShareRedeemPostBody
): Promise<DataBuilderShareRedeemPostResponse> =>
  fetchJson<DataBuilderShareRedeemPostResponse>(
    DATA_BUILDER_ENDPOINTS.shareRedeem,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
